import React, { useState, useEffect } from 'react';
import { clinicsApi } from '../../services/api';
import { useClinic } from '../../context/ClinicContext';
import toast from 'react-hot-toast';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiX,
  HiOutlineOfficeBuilding,
} from 'react-icons/hi';
import ConfirmDialog from '../../components/ConfirmDialog';

const emptyForm = {
  name: '',
  address: '',
  contact_phone: '',
  contact_email: '',
  is_active: true,
};

const Clinics = () => {
  const { refresh: refreshClinicCtx } = useClinic();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null, message: '' });

  useEffect(() => {
    fetchClinics();
  }, []);

  const fetchClinics = async () => {
    setLoading(true);
    try {
      const response = await clinicsApi.getAll();
      setClinics(response.data.data || []);
    } catch {
      toast.error('Failed to fetch clinics');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const handleEdit = (clinic) => {
    setEditing(clinic);
    setFormData({
      name: clinic.name || '',
      address: clinic.address || '',
      contact_phone: clinic.contact_phone || '',
      contact_email: clinic.contact_email || '',
      is_active: clinic.is_active === 1 || clinic.is_active === true,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await clinicsApi.update(editing.id, formData);
        toast.success('Clinic updated successfully');
      } else {
        await clinicsApi.create(formData);
        toast.success('Clinic created successfully');
      }
      setShowForm(false);
      await fetchClinics();
      await refreshClinicCtx();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save clinic');
    }
  };

  const handleDelete = (clinic) => {
    setConfirmDialog({
      isOpen: true,
      message: `Deactivate "${clinic.name}"? Members will lose access until it's reactivated.`,
      onConfirm: async () => {
        try {
          await clinicsApi.delete(clinic.id);
          toast.success('Clinic deactivated');
          await fetchClinics();
          await refreshClinicCtx();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to deactivate clinic');
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Clinics</h1>
          <p className="text-gray-500 mt-1">Manage clinics and their basic details.</p>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <HiPlus className="w-5 h-5" />
          Add Clinic
        </button>
      </div>

      <div className="card">
        {clinics.length === 0 ? (
          <div className="text-center p-8 text-gray-500">
            No clinics yet. Click "Add Clinic" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-4 font-semibold text-gray-700">Clinic</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Address</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Contact</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Created</th>
                  <th className="text-center p-4 font-semibold text-gray-700">Status</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clinics.map((clinic) => (
                  <tr key={clinic.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <HiOutlineOfficeBuilding className="w-5 h-5 text-primary-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{clinic.name}</p>
                          <p className="text-xs text-gray-500 truncate">Clinic ID: {clinic.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-gray-700 max-w-[260px]">
                      <p className="line-clamp-2">{clinic.address || '—'}</p>
                    </td>
                    <td className="p-4 text-gray-600">
                      <div className="text-sm truncate">{clinic.contact_phone || '—'}</div>
                      <div className="text-xs text-gray-500 truncate">{clinic.contact_email || '—'}</div>
                    </td>
                    <td className="p-4 text-sm text-gray-600">
                      <div>{clinic.created_by_name || '—'}</div>
                      <div className="text-xs text-gray-500">
                        {clinic.created_at
                          ? new Date(clinic.created_at).toLocaleDateString()
                          : '—'}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          (clinic.is_active === 1 || clinic.is_active === true)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {(clinic.is_active === 1 || clinic.is_active === true) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(clinic)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                          title="Edit"
                        >
                          <HiPencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(clinic)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Deactivate"
                        >
                          <HiTrash className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto py-4 sm:items-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editing ? 'Edit Clinic' : 'Add Clinic'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Krishna Pediatric Rehabilitation Clinic"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                  <input
                    type="text"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    rows={2}
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-900">Active</span>
                  <p className="text-xs text-gray-500 mt-1">Members can only access active clinics.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, onConfirm: null, message: '' })}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        title="Deactivate Clinic"
        message={confirmDialog.message}
        confirmText="Deactivate"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Clinics;
